import type { SpecType } from '@local/lib';

import { FieldHelpTooltip } from '@components';
import { Group, Select } from '@mantine/core';
import { FileText } from 'lucide-react';
import { useState } from 'react';

import type { TemplateSpecConfig } from '../../quality-formulas/qualityFormulaTypes';

import { useTemplatesForSpec } from '../hooks/useTemplates';
import { fromTemplateConfig } from '../templateConfigConversion';

interface LoadTemplateControlProps {
  onApply: (config: TemplateSpecConfig) => void;
  specLabel: string;
  specType: SpecType;
}

/**
 * Per-spec "load a saved Template" control shown inside each Quality Spec card. Templates are
 * scoped to exactly one Quality Specification (see `templateTypes.ts`), so this only ever lists
 * templates that belong to *this* card's spec — a Moisture card never shows HGI templates, and
 * vice versa, with no per-spec code to maintain as new specs are added (see `useTemplatesForSpec`).
 *
 * This is the only template interaction available here — there's no Save/Delete; those live
 * exclusively on the Template Manager screen so a contract (or another template) can never
 * accidentally overwrite a shared template.
 *
 * Lives in the Templates feature (not `quality-formulas/components`, even though `QualitySpecCard`
 * is the only thing that renders it) since it's the one piece of UI that reaches into the
 * Templates feature's own data layer (`useTemplatesForSpec`/`fromTemplateConfig`) — `QualitySpecCard`
 * never imports it directly; its caller (e.g. `QualityFormulasPage`) supplies it via
 * `renderLoadTemplateControl` instead. Applying the loaded config is the caller's job too (via
 * `onApply`) — this component only ever reads from the Templates feature's own data layer, it
 * never reaches into the quality-formulas store, so the dependency between the two features stays
 * one-directional (Templates never needs to know quality-formulas' store exists).
 */
export const LoadTemplateControl: React.FC<LoadTemplateControlProps> = ({ onApply, specLabel, specType }) => {
  const { isLoading, templates } = useTemplatesForSpec(specType);
  const [selectedTemplateId, setSelectedTemplateId] = useState<null | string>(null);
  const options = templates.map(template => ({ label: template.name, value: template.id }));

  const handleChange = (value: null | string) => {
    setSelectedTemplateId(value);
    const template = templates.find(candidate => candidate.id === value);
    if (template) onApply(fromTemplateConfig(template.config));
  };

  return (
    <Group gap={6} wrap="nowrap">
      <Select
        clearable
        data={options}
        disabled={isLoading}
        leftSection={<FileText size={16} />}
        miw={220}
        nothingFoundMessage="No templates found"
        onChange={handleChange}
        placeholder={templates.length === 0 ? 'No templates yet' : 'Load Template'}
        searchable
        // Only once there's something to load — the "Load Template" placeholder and a selected
        // template's name both default to a dimmed gray that's hard to read, so both go white.
        // "No templates yet" stays dimmed, since that's not actionable text. Set on the `input`
        // part itself (not an ancestor) so it wins outright over Mantine's own placeholder/text
        // color variables, which a plain `style` prop can't reach via `::placeholder`.
        styles={
          templates.length === 0
            ? undefined
            : {
                input: {
                  '--input-placeholder-color': 'var(--mantine-color-white)',
                  color: 'var(--mantine-color-white)',
                },
              }
        }
        value={selectedTemplateId}
      />
      <FieldHelpTooltip
        label={`Load a saved ${specLabel} template's formula as a starting point. Loading never changes the saved template.`}
      />
    </Group>
  );
};
